import { useState, useEffect, useCallback, useRef } from 'react';
import { Bookmark, Category } from '../types/bookmarks';
import { Note, NoteCategory } from '../types/notes';
import { CACHE_UPDATED_EVENT } from './data/useCacheFirstQuery';
import { useCacheStore } from './data/useCacheStore';
import { buildCategoryPath } from '../utils/buildCategoryPath';
import {
  searchNotes,
  searchNotesRegex,
  NoteSearchDoc,
} from '../utils/notesSearch';
import {
  layeredSearch,
  LayeredHit,
  SearchField,
} from '../utils/search/layeredSearch';

export interface SearchDocument {
  bookmark_id: string;
  bookmark_name: string;
  bookmark_url: string;
  notes_plain: string;
  category_name: string;
  category_id: string | null;
}

export interface MatchInfo {
  key: string;
  indices: [number, number][];
  value: string;
}

export interface BookmarkSearchResult {
  kind: 'bookmark';
  bookmark: Bookmark;
  categoryPath: string;
  score: number;
  matches: MatchInfo[];
}

export interface NoteSearchResult {
  kind: 'note';
  note: Note;
  categoryPath: string;
  snippet: string;
  matchStart: number;
  matchEnd: number;
  tier: number;
}

export type SearchResult = BookmarkSearchResult | NoteSearchResult;

export interface SearchOptions {
  /** When true, notes are matched by regex instead of the layered matcher. */
  regexMode?: boolean;
}

interface UseSearchReturn {
  search: (query: string, options?: SearchOptions) => SearchResult[];
  /** Whether any notes exist (drives the regex-toggle visibility). */
  hasNotes: boolean;
  /** True when the last regex-mode query had an invalid pattern. */
  regexInvalid: boolean;
  isIndexReady: boolean;
}

/** Ordered searchable fields for a bookmark document (first = highest precedence). */
function bookmarkFields(doc: SearchDocument): SearchField[] {
  return [
    { name: 'bookmark_name', text: doc.bookmark_name },
    { name: 'bookmark_url', text: doc.bookmark_url },
    { name: 'notes_plain', text: doc.notes_plain },
    { name: 'category_name', text: doc.category_name },
  ];
}

/**
 * Maps a layered-search hit to the view's `BookmarkSearchResult` shape. Pure
 * (no React), so it can be unit-tested directly. `score = tier` (lower is
 * better: 0 exact < 1 prefix < 2 fuzzy). The winning field becomes a single
 * `MatchInfo` with inclusive-end indices (SearchResults slices `end + 1`);
 * `category_name` wins emit no match (the view ignores that key).
 */
export function mapBookmarkHit(
  hit: LayeredHit<SearchDocument>,
  bookmark: Bookmark,
  categoryPath: string,
): BookmarkSearchResult {
  let matches: MatchInfo[] = [];
  if (hit.fieldName !== 'category_name') {
    if (hit.matchIndex >= 0 && hit.matchLength > 0) {
      matches = [
        {
          key: hit.fieldName,
          indices: [[hit.matchIndex, hit.matchIndex + hit.matchLength - 1]],
          value: hit.fieldText,
        },
      ];
    } else {
      // Fuzzy tier (no exact span): still emit the field value with no span so
      // the row renders the raw field text without highlighting.
      matches = [{ key: hit.fieldName, indices: [], value: hit.fieldText }];
    }
  }
  return {
    kind: 'bookmark',
    bookmark,
    categoryPath,
    score: hit.tier,
    matches,
  };
}

export function stripHtml(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

export function useSearch(): UseSearchReturn {
  const [isIndexReady, setIsIndexReady] = useState(false);
  const [hasNotes, setHasNotes] = useState(false);
  const [regexInvalid, setRegexInvalid] = useState(false);
  const documentsRef = useRef<SearchDocument[]>([]);
  const bookmarksRef = useRef<Bookmark[]>([]);
  const categoriesRef = useRef<Category[]>([]);
  const noteDocsRef = useRef<NoteSearchDoc[]>([]);
  const noteCategoriesRef = useRef<NoteCategory[]>([]);
  const cache = useCacheStore();

  const buildIndex = useCallback(() => {
    const bookmarks = cache.getBookmarks();
    const categories = cache.getCategories();

    // Notes corpus (independent of bookmark index readiness).
    const notes = cache.getNotes();
    const noteCategories = cache.getNoteCategories();
    if (notes) {
      const activeNotes = notes.filter((n) => n.deleted_at === null);
      noteDocsRef.current = activeNotes.map((n) => ({
        note: n,
        title: n.note_title,
        plain: stripHtml(n.note_content),
      }));
      noteCategoriesRef.current = noteCategories ?? [];
      setHasNotes(activeNotes.length > 0);
    } else {
      noteDocsRef.current = [];
      setHasNotes(false);
    }

    if (!bookmarks || !categories) {
      setIsIndexReady(false);
      return;
    }

    bookmarksRef.current = bookmarks;
    categoriesRef.current = categories;

    const categoryMap = new Map<string, string>();
    for (const cat of categories) {
      categoryMap.set(cat.category_id, cat.category_name);
    }

    const documents: SearchDocument[] = bookmarks.map((b) => ({
      bookmark_id: b.bookmark_id,
      bookmark_name: b.bookmark_name,
      bookmark_url: b.bookmark_url,
      notes_plain: stripHtml(b.bookmark_notes),
      category_name: categoryMap.get(b.category_id) || '',
      category_id: b.category_id,
    }));

    documentsRef.current = documents;
    setIsIndexReady(true);
  }, [cache]);

  useEffect(() => {
    buildIndex();
  }, [buildIndex]);

  useEffect(() => {
    const handleCacheUpdate = () => {
      buildIndex();
    };

    window.addEventListener(CACHE_UPDATED_EVENT, handleCacheUpdate);
    return () => {
      window.removeEventListener(CACHE_UPDATED_EVENT, handleCacheUpdate);
    };
  }, [buildIndex]);

  const searchBookmarks = useCallback(
    (query: string): BookmarkSearchResult[] => {
      const bookmarkMap = new Map<string, Bookmark>();
      for (const b of bookmarksRef.current) {
        bookmarkMap.set(b.bookmark_id, b);
      }

      const hits = layeredSearch<SearchDocument>({
        query,
        docs: documentsRef.current,
        fieldsOf: bookmarkFields,
        keyOf: (d) => d.bookmark_id,
      });

      const results = hits.map((hit) => {
        const bookmark = bookmarkMap.get(hit.doc.bookmark_id)!;
        const categoryPath = buildCategoryPath(
          hit.doc.category_id,
          categoriesRef.current,
        );
        return {
          fieldIndex: hit.fieldIndex,
          result: mapBookmarkHit(hit, bookmark, categoryPath),
        };
      });

      // Exact-before-fuzzy within bookmarks (score = tier), then name-field
      // precedence (fieldIndex), then alphabetical by name.
      return results
        .sort((a, b) => {
          if (a.result.score !== b.result.score)
            return a.result.score - b.result.score;
          if (a.fieldIndex !== b.fieldIndex) return a.fieldIndex - b.fieldIndex;
          return a.result.bookmark.bookmark_name.localeCompare(
            b.result.bookmark.bookmark_name,
          );
        })
        .map((r) => r.result);
    },
    [],
  );

  const search = useCallback(
    (query: string, options?: SearchOptions): SearchResult[] => {
      if (!query || query.trim() === '') {
        setRegexInvalid(false);
        return [];
      }

      const regexMode = options?.regexMode ?? false;

      // Note results (layered matcher or regex).
      let noteResults: NoteSearchResult[] = [];
      if (regexMode) {
        const hits = searchNotesRegex(query, noteDocsRef.current);
        if (hits === null) {
          setRegexInvalid(true);
          // Invalid pattern: no note results, but still show bookmark matches
          // (which don't use regex).
        } else {
          setRegexInvalid(false);
          noteResults = hits.map((h) => ({
            kind: 'note' as const,
            note: h.note,
            categoryPath: buildCategoryPath(
              h.note.category_id,
              noteCategoriesRef.current as unknown as Category[],
            ),
            snippet: h.snippet,
            matchStart: h.matchStart,
            matchEnd: h.matchEnd,
            tier: h.tier,
          }));
        }
      } else {
        setRegexInvalid(false);
        noteResults = searchNotes(query, noteDocsRef.current).map((h) => ({
          kind: 'note' as const,
          note: h.note,
          categoryPath: buildCategoryPath(
            h.note.category_id,
            noteCategoriesRef.current as unknown as Category[],
          ),
          snippet: h.snippet,
          matchStart: h.matchStart,
          matchEnd: h.matchEnd,
          tier: h.tier,
        }));
      }

      // In regex mode, bookmarks are skipped (notes-only); otherwise the shared
      // layered engine handles bookmark search.
      const bookmarkResults = regexMode ? [] : searchBookmarks(query);

      return [...noteResults, ...bookmarkResults];
    },
    [searchBookmarks],
  );

  return { search, hasNotes, regexInvalid, isIndexReady };
}
